import { useContent } from "../contexts/ContentContext";
import { openEnrollDialog } from "../hooks/useEnroll";
import {
  Users, Percent, CalendarCheck, Sparkles, ShieldCheck, Award,
  BookOpenCheck, GraduationCap, FlaskConical, Pill, FlaskRound, Microscope,
  Stethoscope, Beaker, Armchair, Gift, Tag, Star, Trophy, Flame, Clock, Phone, MessageCircle,
} from "lucide-react";

const ICONS = {
  Users, Percent, CalendarCheck, Sparkles, ShieldCheck, Award, BookOpenCheck,
  GraduationCap, FlaskConical, Pill, FlaskRound, Microscope, Stethoscope, Beaker,
  Armchair, Gift, Tag, Star, Trophy, Flame, Clock, Phone, MessageCircle,
};

function OfferIcon({ name, className }) {
  const Cmp = ICONS[name] || Sparkles;
  return <Cmp className={className} />;
}

function actionHref(action, settings) {
  if (action === "call") return `tel:${settings.phone || ''}`;
  if (action === "whatsapp") {
    const msg = encodeURIComponent(settings.whatsapp_message || "Hi, I'm interested.");
    return `https://wa.me/${settings.whatsapp || ''}?text=${msg}`;
  }
  return null;
}

export default function Offers() {
  const { content } = useContent();
  const o = content?.offers || {};
  const settings = content?.settings || {};
  const items = o.items || [];
  if (o.enabled === false || items.length === 0) return null;

  return (
    <section
      id="offers"
      className="relative py-20 lg:py-28 overflow-hidden"
      style={{ background: "#0b1442" }}
      data-testid="offers-section"
    >
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage:
          "radial-gradient(circle at 20% 20%, #ffffff 0px, transparent 60%), radial-gradient(circle at 80% 80%, #facc15 0px, transparent 60%)",
      }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl text-white">
          <div className="overline" style={{ color: "rgba(255,255,255,0.85)" }}>{o.eyebrow}</div>
          <h2 className="font-display mt-3 text-3xl lg:text-4xl font-medium tracking-tight">
            {o.heading}
          </h2>
          {o.subheading && (
            <p className="mt-4 text-white/70 leading-relaxed">{o.subheading}</p>
          )}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {items.map((card, i) => {
            const isCenter = i === Math.floor(items.length / 2) && items.length >= 3;
            const href = actionHref(card.button_action, settings);
            const ButtonInner = (
              <span className="inline-flex items-center justify-center w-full">
                {card.button_label}
              </span>
            );
            return (
              <div
                key={card.id || i}
                data-testid={`offer-card-${card.id || i}`}
                className={`relative rounded-2xl overflow-hidden p-8 lg:p-10 flex flex-col transition-all duration-500 hover:-translate-y-1.5 ${
                  isCenter ? "md:scale-[1.06] shadow-2xl ring-1 ring-white/10" : "shadow-xl"
                }`}
                style={{
                  backgroundColor: card.background_color || "#1e2c6b",
                  color: card.text_color || "#ffffff",
                }}
              >
                {card.ribbon && (
                  <div className="absolute top-0 right-0 pointer-events-none">
                    <div
                      className="absolute top-6 -right-10 rotate-45 px-12 py-1 text-[10px] font-semibold tracking-[0.22em] uppercase"
                      style={{ backgroundColor: "#0b1442", color: "#ffffff" }}
                    >
                      {card.ribbon}
                    </div>
                  </div>
                )}

                <div className="flex flex-col items-center text-center flex-1">
                  <div
                    className="h-14 w-14 rounded-full flex items-center justify-center mb-6"
                    style={{ backgroundColor: "rgba(255,255,255,0.18)" }}
                  >
                    <OfferIcon name={card.icon} className="h-7 w-7" />
                  </div>

                  {card.value && (
                    <div className="font-display text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-3">
                      {card.value}
                    </div>
                  )}

                  <h3 className="font-display text-xl lg:text-2xl font-semibold tracking-tight leading-tight">
                    {card.title}
                  </h3>

                  {card.body && (
                    <p className="mt-4 text-sm lg:text-base leading-relaxed opacity-90 max-w-xs">
                      {card.body}
                    </p>
                  )}

                  {card.pill && (
                    <div
                      className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs lg:text-sm font-medium"
                      style={{ backgroundColor: "rgba(255,255,255,0.18)" }}
                    >
                      <Armchair className="h-4 w-4" />
                      {card.pill}
                    </div>
                  )}
                </div>

                <div className="mt-8">
                  {href ? (
                    <a
                      href={href}
                      target={href.startsWith("http") ? "_blank" : undefined}
                      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                      data-testid={`offer-cta-${card.id || i}`}
                      className="block w-full h-12 rounded-full text-sm font-semibold tracking-wide leading-[3rem] text-center hover:opacity-95 transition"
                      style={{
                        backgroundColor: card.button_bg_color || "#ffffff",
                        color: card.button_text_color || "#0b1442",
                      }}
                    >
                      {ButtonInner}
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={openEnrollDialog}
                      data-testid={`offer-cta-${card.id || i}`}
                      className="block w-full h-12 rounded-full text-sm font-semibold tracking-wide text-center hover:opacity-95 transition"
                      style={{
                        backgroundColor: card.button_bg_color || "#ffffff",
                        color: card.button_text_color || "#0b1442",
                      }}
                    >
                      {ButtonInner}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
